<script setup lang="ts">
import { computed, ref } from 'vue'
import type { VpnKey } from '@/types'
import { useUserStore } from '@/stores/user'
import QrModal from '@/components/keys/QrModal.vue'
import KeyConfigModal from '@/components/keys/KeyConfigModal.vue'
import ExtendKeyModal from '@/components/keys/ExtendKeyModal.vue'
import ManualKeyRow from '@/components/keys/ManualKeyRow.vue'
import SwitchServerModal from '@/components/switch/SwitchServerModal.vue'
import LocationDisplay from '@/components/ui/LocationDisplay.vue'
import { formatDateTime } from '@/utils/format'
import { expiryBadge, groupHeaderKey, keyIdentifier } from '@/utils/keys'

const props = defineProps<{ keys: VpnKey[] }>()
const user = useUserStore()

const firstKey = computed(() => groupHeaderKey(props.keys))
const isExpiredGroup = computed(() => Boolean(firstKey.value?.is_expired))
const badge = computed(() => firstKey.value ? expiryBadge(firstKey.value) : { className: 'badge bg-secondary', text: '—' })

const qrOpen = ref(false)
const qrData = ref('')
const qrTitle = ref('')
const keyOpen = ref(false)
const keyConfig = ref('')
const keyTitle = ref('')
const extendOpen = ref(false)
const switchOpen = ref(false)

const canSwitch = computed(() =>
  firstKey.value
  && !firstKey.value.is_subscription_key
  && !firstKey.value.is_expired
  && user.serverSwitchEnabled,
)
const canExtend = computed(() => firstKey.value && !firstKey.value.is_subscription_key)

function openQr(key: VpnKey) {
  qrData.value = key.qr_data || key.config_string || ''
  qrTitle.value = key.server_name
  qrOpen.value = true
}

function openKey(key: VpnKey) {
  keyConfig.value = key.config_string || ''
  keyTitle.value = key.server_name
  keyOpen.value = true
}
</script>

<template>
  <div
    class="panel !p-0 overflow-hidden border border-white-light"
    :class="isExpiredGroup ? 'border-danger/30 bg-danger-light/20' : ''"
  >
    <div class="p-3">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div class="text-sm leading-relaxed">
          <strong>Сервер:</strong> {{ firstKey?.server_name }}
          <span class="text-muted"> · </span>
          <strong>Локация:</strong>
          <LocationDisplay
            :country-code="firstKey?.country_code"
            :location="firstKey?.location"
            inline
            :size="20"
          />
          <span class="text-muted"> · </span>
          <strong>ключ:</strong> {{ keyIdentifier(firstKey!) }}
          <span class="text-muted"> · </span>
          <strong>Срок действия:</strong> {{ formatDateTime(firstKey?.expires_at || null) }}
          <span :class="badge.className" class="ml-1">{{ badge.text }}</span>
        </div>
        <div class="flex flex-wrap gap-2">
          <button
            v-if="canExtend"
            type="button"
            class="btn btn-sm btn-outline-success"
            @click="extendOpen = true"
          >
            Продлить
          </button>
          <button
            v-if="canSwitch"
            type="button"
            class="btn btn-sm btn-outline-warning"
            @click="switchOpen = true"
          >
            Сменить сервер
          </button>
        </div>
      </div>
    </div>

    <div v-if="!isExpiredGroup" class="border-t border-white-light px-3">
      <ManualKeyRow
        v-for="key in keys"
        :key="key.id"
        :key-item="key"
        @show-key="openKey"
        @show-qr="openQr"
      />
    </div>

    <QrModal :open="qrOpen" :data="qrData" :title="qrTitle" @close="qrOpen = false" />
    <KeyConfigModal :open="keyOpen" :config="keyConfig" :title="keyTitle" @close="keyOpen = false" />
    <ExtendKeyModal :open="extendOpen" :key-id="firstKey!.id" @close="extendOpen = false" />
    <SwitchServerModal :open="switchOpen" :key-id="firstKey!.id" @close="switchOpen = false" />
  </div>
</template>
